/**
 * Formatting utilities.
 */
import { formatDistanceToNow, format } from 'date-fns'

/** Flatten a DRF error body ({field: {nested: ["msg"]}}) into "field.nested: msg" lines. */
export function flattenApiErrors(data: unknown, prefix = ''): string[] {
  if (typeof data === 'string') return [prefix ? `${prefix}: ${data}` : data]
  if (Array.isArray(data)) return data.flatMap((d) => flattenApiErrors(d, prefix))
  if (data && typeof data === 'object') {
    return Object.entries(data).flatMap(([k, v]) =>
      flattenApiErrors(v, k === 'detail' || k === 'non_field_errors' ? prefix : prefix ? `${prefix}.${k}` : k),
    )
  }
  return []
}

export function formatApiError(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  const lines = flattenApiErrors(data)
  if (lines.length) return lines.join('; ')
  return error instanceof Error ? error.message : 'Unknown error'
}

export function formatRelative(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true })
  } catch {
    return dateString
  }
}

export function formatDateTime(dateString: string): string {
  try {
    return format(new Date(dateString), 'MMM d, yyyy HH:mm')
  } catch {
    return dateString
  }
}

export function formatDate(dateString: string): string {
  try {
    return format(new Date(dateString), 'MMM d, yyyy')
  } catch {
    return dateString
  }
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function formatFamily(family: string): string {
  const labels: Record<string, string> = {
    appcentral: 'AppCentral',
    aip: 'AIP',
    pay_edi: 'Pay EDI',
    m2m: 'M2M',
    agent_central: 'Agent Central',
    genai: 'GenAI',
    fixed_assets: 'Fixed Assets',
    apprise: 'Apprise',
    custom: 'Custom',
  }
  return labels[family] ?? family
}

export function formatEnvironment(env: string): string {
  const labels: Record<string, string> = {
    dev: 'Development',
    qa: 'QA',
    uat: 'UAT',
    demo: 'Demo',
    production: 'Production',
    uat_usgov: 'USGOV UAT',
    prod_usgov: 'USGOV Prod',
  }
  return labels[env] ?? env
}

export function formatStrategy(strategy: string): string {
  const labels: Record<string, string> = {
    rolling: 'Rolling Update',
    blue_green: 'Blue/Green',
    canary: 'Canary',
    recreate: 'Recreate',
  }
  return labels[strategy] ?? strategy
}

export const DEPLOYMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  validating: 'Validating',
  provisioning_network: 'Provisioning Network',
  provisioning_infra: 'Provisioning Infra',
  planning_infra: 'Planning Infrastructure',
  awaiting_plan_approval: 'Awaiting Plan Approval',
  applying_infra: 'Applying Infrastructure',
  verifying_server: 'Verifying Server',
  infra_ready: 'Infrastructure Ready',
  deploying_app: 'Deploying App',
  configuring: 'Configuring',
  configuring_haproxy: 'Configuring HAProxy',
  configuring_cloudflare: 'Configuring Cloudflare',
  post_deploy_config: 'Post-Deploy Config',
  configuring_integrations: 'Integrations',
  switching_traffic: 'Switching Traffic',
  running_health_checks: 'Health Checks',
  completed: 'Completed',
  failed: 'Failed',
  rolled_back: 'Rolled Back',
  cancelled: 'Cancelled',
}

export function formatDeploymentStatus(status: string): string {
  return DEPLOYMENT_STATUS_LABELS[status] ?? status.replace(/_/g, ' ')
}

export function isActiveStatus(status: string): boolean {
  return [
    'validating',
    'provisioning_infra',
    'planning_infra',
    'applying_infra',
    'verifying_server',
    'awaiting_plan_approval',
    'deploying_app',
    'configuring',
    'running_health_checks',
  ].includes(status)
}
